import React, { useCallback, useMemo, useState } from 'react'
import { Modal, Button, Form, ButtonToolbar, Message, Loader, Input, SelectPicker, InputNumber } from 'rsuite'
import { VehicleInfo } from '@@partner:types:app'

import { useMutateUpdateVehicleInfo } from '@@partner:graphql/mutation/vehicle/updateVehicle.gql'
import { useFetchVehicleTypes } from '@@partner:graphql/query/vehicle/vehicleTypes.gql'
import { mapStateToApiVariable } from './VehicleFormState'

export type AddVehicleFormData = Omit<VehicleInfo, 
  'id' |
  'created_at' |
  'updated_at' |
  'type' |
  'courierShifts' |
  'vehicleIncidents'
>

type AddVehicleModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave?: () => void
}

const defaultFormValue: AddVehicleFormData = {
  vehicle_type_id: 1,
  license_plate: '',
  vin: '',
  model: '',
  brand: '',
  year: new Date().getFullYear(),
  color: '',
  capacity_kg: 300,
  capacity_m3: 10,
  temp_max_c: 100,
  temp_min_c: -40,
  range_km: 1000,
  average_speed: 60,
  is_active: true,
  comment: ''
}

// Регулярное выражение для проверки формата времени (HH:MM)
const timePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

export default function AddVehicleModal ({
  isOpen,
  onClose,
  onSave
}: AddVehicleModalProps) {
  const [ formValue, setFormValue ] = React.useState<AddVehicleFormData>(defaultFormValue)
  const [ formError, setFormError ] = useState<Error | null>(null)

  const { vehicleTypes, loading: loadingVehicleTypes, error: errorLoadingVehicleTypes } = useFetchVehicleTypes()

  const preparedVehicleTypes = useMemo(() => vehicleTypes.map(type => ({
    label: type.name,
    value: type.id
  })), [ vehicleTypes ])

  const [ createVehicle, { loading, error } ] = useMutateUpdateVehicleInfo()

  const handleFormChange = (formValue: Record<string, any>) => {
    setFormValue(formValue as AddVehicleFormData)
  }


  const handleSubmit = useCallback(async () => {
    
    try { 
      await createVehicle({
        variables: {
          upsert: true,
          newData: mapStateToApiVariable(formValue)
        }
      })
      setFormError(null)
      if (onSave) { 
        onSave() 
      }
      onClose()
    } 
    catch (error) {
      setFormError(error as Error)
      console.error('Failed to save shift:', error)
    }
  }, [ formValue, onClose, onSave, createVehicle ])

  return (
    <Modal open={isOpen} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Зарегистрировать траспорт</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {(formError || error) && (
          <Message type="error" className="u-mb-md">
            {formError ? formError.message : error?.message}
          </Message>
        )}
        
        <Form
          fluid
          formValue={formValue}
          onChange={setFormValue}
          onSubmit={handleSubmit}
        >
          <Form.Group>
            <Form.ControlLabel>Тип транспорта</Form.ControlLabel>
            <SelectPicker
              data={preparedVehicleTypes}
              labelKey="label"
              valueKey="value"
              onChange={(value) => setFormValue({ ...formValue, vehicle_type_id: value as number })}
              cleanable={false}
              searchable={false}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Гос. номер</Form.ControlLabel>
            <Form.Control
              name="license_plate"
              accepter={Input}
              placeholder="A123BC"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>VIN номер</Form.ControlLabel>
            <Form.Control
              name="vin"
              accepter={Input}
              placeholder="WAUZZZ8K0DA123456"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Марка</Form.ControlLabel>
            <Form.Control
              name="brand"
              accepter={Input}
              placeholder="Toyota"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Модель</Form.ControlLabel>
            <Form.Control
              name="model"
              accepter={Input}
              placeholder="Corolla"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Год выпуска</Form.ControlLabel>
            <Form.Control
              name="year"
              accepter={InputNumber}
              type="number"
              placeholder="2023"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Цвет</Form.ControlLabel>
            <Form.Control
              name="color"
              accepter={Input}
              placeholder="Белый"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Грузоподъёмность (кг)</Form.ControlLabel>
            <Form.Control
              name="capacity_kg"
              accepter={InputNumber}
              type="number"
              placeholder="300"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Объём (м³)</Form.ControlLabel>
            <Form.Control
              name="capacity_m3"
              accepter={InputNumber}
              type="number"
              placeholder="10"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Максимальная температура (°C)</Form.ControlLabel>
            <Form.Control
              name="temp_max_c"
              accepter={InputNumber}
              type="number"
              placeholder="100"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Минимальная температура (°C)</Form.ControlLabel>
            <Form.Control
              name="temp_min_c"
              accepter={InputNumber}
              type="number"
              placeholder="-40"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Запас хода (км)</Form.ControlLabel>
            <Form.Control
              name="range_km"
              accepter={InputNumber}
              type="number"
              placeholder="1000"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Средняя скорость (км/ч)</Form.ControlLabel>
            <Form.Control
              name="average_speed"
              accepter={InputNumber}
              type="number"
              placeholder="60"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Активен</Form.ControlLabel>
            <div style={{ paddingTop: '8px' }}>
              <input
                type="checkbox"
                checked={formValue.is_active}
                onChange={(e) => setFormValue({...formValue, is_active: e.target.checked})}
              />
            </div>
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Комментарий</Form.ControlLabel>
            <Form.Control
              name="comment"
              accepter={Input}
              rows={3}
              placeholder="Дополнительная информация о транспорте"
            />
          </Form.Group>
        </Form>

        {loading && <Loader center content="Сохранение..." />}
      </Modal.Body>

      <Modal.Footer>
        <ButtonToolbar>
          <Button appearance="subtle" onClick={onClose}>
            Отмена
          </Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={loading}>
            Создать
          </Button>
        </ButtonToolbar>
      </Modal.Footer>
    </Modal>
  )
}
